import Agent from 'services/api/Agent';
import AgentMetric from 'services/api/AgentMetric';
import AgentSkill from 'services/api/AgentSkill';
import AgentTag from 'services/api/AgentTag';
import AgentTool from 'services/api/AgentTool';
import KnowledgeApi from 'services/api/Knowledge';
import AgentWorkspace from 'components/@home/@settings/Tabs/Agents/AgentWorkspace';
import Loading from 'components/common/Loading';
import { Box } from '@mui/material';
import PreviewPanel from 'components/@home/@settings/Tabs/Agents/PreviewPanel';
import { func, object, string } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useAgenticRoute } from 'utils/agenticRoute';

const ChatFlowAgentEdit = ({
  agentId,
  flow,
  actions = null,
  onBackToList = null,
  chatFlows,
  chatFlowEntityTypes,
  chatFlowIntents,
  chatFlowIntegrations,
  authCompany = null,
  onCreateSkill = null,
  onCreateTool = null,
  onCreateKnowledge = null,
}) => {
  const [agent, setAgent] = useState(null);
  const [skills, setSkills] = useState([]);
  const [tools, setTools] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [tags, setTags] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const route = useAgenticRoute(authCompany);

  const load = useCallback(async () => {
    if (!agentId) return;
    setLoadError(false);
    try {
      const [a, sk, tl, kn, tg, mt] = await Promise.all([
        Agent.get(agentId),
        AgentSkill.list(),
        AgentTool.list(),
        KnowledgeApi.list(),
        AgentTag.list(),
        AgentMetric.list(),
      ]);
      setAgent(a);
      setSkills(sk || []);
      setTools(tl || []);
      setKnowledge(kn || []);
      setTags(tg || []);
      setMetrics(mt || []);
    } catch {
      setLoadError(true);
      setAgent(null);
    }
  }, [agentId]);

  const reloadKnowledge = async () => {
    try {
      const kn = await KnowledgeApi.list();
      setKnowledge(kn || []);
    } catch {
      // keep previous list on transient failure
    }
  };

  const reloadTags = async () => {
    try {
      const tg = await AgentTag.list();
      setTags(tg || []);
    } catch {
      // ignore — UI keeps its previous state on transient failures
    }
  };

  const reloadMetrics = async () => {
    try {
      const mt = await AgentMetric.list();
      setMetrics(mt || []);
    } catch {
      // ignore — UI keeps its previous state on transient failures
    }
  };

  const reloadSkills = async () => {
    try {
      const sk = await AgentSkill.list();
      setSkills(sk || []);
    } catch {
      // ignore — UI keeps its previous state on transient failures
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (route.section === 'knowledge') {
      reloadKnowledge();
    }
  }, [route.section, route.knowledgeId]);

  const saveAgent = async patch => {
    const updated = await Agent.updateDraft(agentId, patch);
    setAgent(updated);
    if (patch.name !== undefined && flow?._id && actions?.chatFlows) {
      await actions.chatFlows.update(flow._id, { name: patch.name });
      await actions.chatFlows.load();
    }
  };

  const saveFlowColor = async color => {
    if (!flow?._id || !actions?.chatFlows) return;
    await actions.chatFlows.update(flow._id, { color });
    await actions.chatFlows.load();
  };

  if (!flow) return null;

  if (!agent) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 320,
        }}
      >
        {loadError ? (
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            Could not load this agent.
          </Box>
        ) : (
          <Loading size={48} />
        )}
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          flex: '1 1 0',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <AgentWorkspace
          flow={flow}
          agentState={{ agent, skills, tools, knowledge, tags, metrics }}
          onReloadTags={reloadTags}
          onReloadMetrics={reloadMetrics}
          onReloadKnowledge={reloadKnowledge}
          onReloadSkills={reloadSkills}
          onBackToList={onBackToList}
          chatFlows={chatFlows}
          chatFlowEntityTypes={chatFlowEntityTypes}
          chatFlowIntents={chatFlowIntents}
          chatFlowIntegrations={chatFlowIntegrations}
          onSaveAgent={saveAgent}
          onSaveFlowColor={saveFlowColor}
          onTest={() => setPreviewOpen(true)}
          onCreateSkill={onCreateSkill}
          onCreateTool={onCreateTool}
          onCreateKnowledge={onCreateKnowledge}
          authCompany={authCompany}
        />
      </Box>
      <PreviewPanel agentId={agentId} open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </>
  );
};

ChatFlowAgentEdit.propTypes = {
  agentId: string.isRequired,
  flow: object.isRequired,
  actions: object,
  onBackToList: func,
  chatFlows: object.isRequired,
  chatFlowEntityTypes: object.isRequired,
  chatFlowIntents: object.isRequired,
  chatFlowIntegrations: object.isRequired,
  authCompany: object,
  onCreateSkill: func,
  onCreateTool: func,
  onCreateKnowledge: func,
};

export default ChatFlowAgentEdit;
